<script setup lang="ts">
import { computed } from "vue";
import { bss } from "@dashboard/base/BssService";
import type { BrandBssKeys } from "@dashboard/brands/brandConfig";

interface Props {
  logoId: string;
  logoHref?: string;
  logoElement?: "a" | "div";
  bssKeys?: BrandBssKeys;
}

const props = withDefaults(defineProps<Props>(), {
  logoHref: "",
  logoElement: "a",
});

// ATT and BT keep their original BSS keys and getter names.
const mainLogoBinding = computed(() =>
  props.bssKeys?.mainLogoUrl ?? props.bssKeys?.serviceSiteMainLogo,
);
const hasLogo = computed(() => {
  const binding = mainLogoBinding.value;
  return binding ? bss.has(binding.key) : false;
});
const mainLogoUrl = computed(() => {
  const binding = mainLogoBinding.value;
  return binding ? bss.get(binding.key, binding.fallback ?? "") : "";
});
const brandName = computed(() => {
  const binding = props.bssKeys?.brandName;
  return binding ? bss.get(binding.key, binding.fallback ?? "") : "";
});
const supportLink = computed(() => {
  const binding = props.bssKeys?.supportLink ?? props.bssKeys?.feedbackPage;
  return binding ? bss.get(binding.key, binding.fallback ?? props.logoHref) : props.logoHref;
});
</script>

<template>
  <header class="rc-layout">
    <div class="rc-layout-inner flex row space-between align-center">
      <div v-if="props.logoElement === 'a'">
        <a
          :id="props.logoId"
          :href="supportLink || undefined"
          class="CId-link-main-logo IId-link-main-logo"
          :class="[props.logoId, { 'has-bss-logo': hasLogo }]"
          :target="supportLink ? '_blank' : undefined"
          :rel="supportLink ? 'noopener' : undefined"
          data-dtm-name="Logo Service Status"
        >
          <img v-if="hasLogo" :src="mainLogoUrl" :alt="brandName">
        </a>
      </div>
      <div v-else :id="props.logoId" :class="props.logoId" />
    </div>
  </header>
</template>
